import React, { useCallback } from 'react';
import ActionSelect from '../../ActionSelect';
import Button, { ButtonSizes, ButtonStyles } from '../../Button/Button';
import Typography, {
  TypographyVariants,
  TypographyWeights,
} from '../../../ui-components/Typography/Typography';
import { TRANSLATIONS } from '../../../../translations';
import { ORDER_STATUSES, ORDER_ACTIONS } from '../../../define/constants';

const ReturnOrderButtons = ({
  token,
  order,
  setOrder,
  onClickDetails,
  setIsModalTransformVoucherOpen,
  setIsModalCancelVoucherOpen,
  setIsModalDownloadFileOpen,
  setIsModalRefundOpen,
  setIsModalRescheduleOpen,
  setIsModalOrderIssueOpen,
  setIsModalBookingCompletedToVoucherOpen,
  setIsModalCancelGiftcardOpen,
  setIsModalResendEmailOpen,
  setIsModalDeleteBookingOnHoldOpen,
  setIsModalOnHoldToVoucherOpen,
  setIsModalOnHoldRescheduleOpen,
  setIsModalOnHoldToGiftcardOpen,
}) => {
  const orderItems = order?.items || [];

  const allowedOperations =
    order && order.customerAllowedOperations
      ? order.customerAllowedOperations
      : [];

  let blockReschedule = false;

  for (let i = 0; i < orderItems.length; i++) {
    if (
      orderItems[i].priceSelector === undefined ||
      orderItems[i].priceSelector === null
    ) {
      blockReschedule = true;
      break;
    }
  }

  const detailsArray = new Set([
    ORDER_STATUSES.onHoldPartner,
    ORDER_STATUSES.onHoldAlternative,
    ORDER_STATUSES.onHoldRefused,
    ORDER_STATUSES.onHoldExpired,
    ORDER_STATUSES.withdrawn,
    ORDER_STATUSES.confirmedBooking,
    ORDER_STATUSES.completedBooking,
    ORDER_STATUSES.refunded,
    ORDER_STATUSES.cancelled,
    ORDER_STATUSES.refused,
    ORDER_STATUSES.confirmedSuspended,
    ORDER_STATUSES.confirmedVoucher,
    ORDER_STATUSES.completedVoucher,
    ORDER_STATUSES.expiredVoucher,
  ]);

  const setOrderAndOpenModal = useCallback(
    (modalSetter) => {
      setOrder(order);
      modalSetter(true);
    },
    [order, setOrder]
  );

  // return the allowed actions for each order status
  const returnButtons = useCallback(() => {
    const { status } = order;
    const buttons = [];
    const options = [];
    const actions = {};

    if (detailsArray.has(status)) {
      buttons.push(
        <Button
          key="details"
          size={ButtonSizes.xs}
          style={ButtonStyles.empty}
          label={
            <Typography
              variant={TypographyVariants.p3}
              weight={TypographyWeights.bold}
            >
              {app_main_wpml.details}
            </Typography>
          }
          onClick={() => onClickDetails(order)}
        />
      );
    }

    if (!token) return buttons;

    if (allowedOperations) {
      allowedOperations.forEach((operation) => {
        switch (operation) {
          case ORDER_ACTIONS.VOUCHER_WITHDRAW:
            options.push({
              value: ORDER_ACTIONS.VOUCHER_WITHDRAW,
              label: TRANSLATIONS.CANCEL_VOUCHER,
              icon: 'block',
              position: 3,
            });
            actions[ORDER_ACTIONS.VOUCHER_WITHDRAW] = () =>
              setOrderAndOpenModal(setIsModalCancelVoucherOpen);
            break;
          case ORDER_ACTIONS.VOUCHER_TO_GIFTCARD:
            options.push({
              value: ORDER_ACTIONS.VOUCHER_TO_GIFTCARD,
              label: TRANSLATIONS.CHANGE_ACTIVITY,
              icon: 'cancellation',
              position: 1,
            });

            actions[ORDER_ACTIONS.VOUCHER_TO_GIFTCARD] = () =>
              setOrderAndOpenModal(setIsModalTransformVoucherOpen);
            break;
          case ORDER_ACTIONS.ONHOLD_TO_GIFTCARD:
            options.push({
              value: ORDER_ACTIONS.ONHOLD_TO_GIFTCARD,
              label: TRANSLATIONS.TRANSFORM_TO_GIFTCARD,
              icon: 'giftcard',
              position: 2,
            });
            actions[ORDER_ACTIONS.ONHOLD_TO_GIFTCARD] = () =>
              setOrderAndOpenModal(setIsModalOnHoldToGiftcardOpen);
            break;
          case ORDER_ACTIONS.ONHOLD_TO_VOUCHER:
            options.push({
              value: ORDER_ACTIONS.ONHOLD_TO_VOUCHER,
              label: TRANSLATIONS.TRANSFORM_TO_VOUCHER,
              icon: 'ticket',
              position: 1,
            });
            actions[ORDER_ACTIONS.ONHOLD_TO_VOUCHER] = () =>
              setOrderAndOpenModal(setIsModalOnHoldToVoucherOpen);
            break;
          case ORDER_ACTIONS.ONHOLD_NEW_DATE:
            if (!blockReschedule) {
              options.push({
                value: ORDER_ACTIONS.ONHOLD_NEW_DATE,
                label: TRANSLATIONS.RESCHEDULE_THE_ACTIVITY,
                icon: 'reschedule',
                position: 0,
              });
              actions[ORDER_ACTIONS.ONHOLD_NEW_DATE] = () =>
                setOrderAndOpenModal(setIsModalOnHoldRescheduleOpen);
            }
            break;
          case ORDER_ACTIONS.ONHOLD_REFUND:
            options.push({
              value: ORDER_ACTIONS.ONHOLD_REFUND,
              label: TRANSLATIONS.CANCEL_RESERVATION,
              icon: 'block',
              position: 3,
            });
            actions[ORDER_ACTIONS.ONHOLD_REFUND] = () =>
              setOrderAndOpenModal(setIsModalDeleteBookingOnHoldOpen);
            break;
          case ORDER_ACTIONS.COMPLETED_BOOKING_REFUND:
            options.push({
              value: ORDER_ACTIONS.COMPLETED_BOOKING_REFUND,
              label: TRANSLATIONS.REQUEST_REFUND,
              icon: 'refund',
              position: 2,
            });
            actions[ORDER_ACTIONS.COMPLETED_BOOKING_REFUND] = () =>
              setOrderAndOpenModal(setIsModalRefundOpen);
            break;
          case ORDER_ACTIONS.COMPLETED_BOOKING_TO_VOUCHER:
            options.push({
              value: ORDER_ACTIONS.COMPLETED_BOOKING_TO_VOUCHER,
              label: TRANSLATIONS.REQUEST_BOOKING_TO_VOUCHER,
              icon: 'ticket',
              position: 0,
            });
            actions[ORDER_ACTIONS.COMPLETED_BOOKING_TO_VOUCHER] = () =>
              setOrderAndOpenModal(setIsModalBookingCompletedToVoucherOpen);
            break;
          case ORDER_ACTIONS.COMPLETED_BOOKING_NEW_DATE:
            if (!blockReschedule) {
              options.push({
                value: [ORDER_ACTIONS.COMPLETED_BOOKING_NEW_DATE],
                label: TRANSLATIONS.RESCHEDULE_THE_ACTIVITY,
                icon: 'reschedule',
                position: 1,
              });
              actions[ORDER_ACTIONS.COMPLETED_BOOKING_NEW_DATE] = () =>
                setOrderAndOpenModal(setIsModalRescheduleOpen);
            }
            break;
          case ORDER_ACTIONS.GIFTCARD_DELETE:
            options.push({
              value: [ORDER_ACTIONS.GIFTCARD_DELETE],
              label: TRANSLATIONS.CANCEL_GIFTCARD,
              icon: 'block',
              position: 1,
            });
            actions[ORDER_ACTIONS.GIFTCARD_DELETE] = () =>
              setOrderAndOpenModal(setIsModalCancelGiftcardOpen);
            break;
          case ORDER_ACTIONS.CONFIRMED_BOOKING_REFUND:
            options.push({
              value: ORDER_ACTIONS.CONFIRMED_BOOKING_REFUND,
              label: TRANSLATIONS.CANCEL_RESERVATION,
              icon: 'block',
              position: 98,
            });
            actions[ORDER_ACTIONS.CONFIRMED_BOOKING_REFUND] = () =>
              setOrderAndOpenModal(setIsModalDeleteBookingOnHoldOpen);
            break;
          case ORDER_ACTIONS.CONFIRMED_BOOKING_NEW_DATE:
            if (!blockReschedule) {
              options.push({
                value: ORDER_ACTIONS.CONFIRMED_BOOKING_NEW_DATE,
                label: TRANSLATIONS.RESCHEDULE_THE_ACTIVITY,
                icon: 'reschedule',
                position: 1,
              });

              actions[ORDER_ACTIONS.CONFIRMED_BOOKING_NEW_DATE] = () =>
                setOrderAndOpenModal(setIsModalOnHoldRescheduleOpen);
            }
            break;
          case ORDER_ACTIONS.CONFIRMED_BOOKING_TO_VOUCHER:
            options.push({
              value: ORDER_ACTIONS.CONFIRMED_BOOKING_TO_VOUCHER,
              label: TRANSLATIONS.REQUEST_BOOKING_TO_VOUCHER,
              icon: 'ticket',
              position: 0,
            });

            actions[ORDER_ACTIONS.CONFIRMED_BOOKING_TO_VOUCHER] = () =>
              setOrderAndOpenModal(setIsModalOnHoldToVoucherOpen);
            break;
          case ORDER_ACTIONS.CONFIRMED_BOOKING_TO_GIFTCARD:
            options.push({
              value: ORDER_ACTIONS.CONFIRMED_BOOKING_TO_GIFTCARD,
              label: TRANSLATIONS.REQUEST_TRANSFORMATION_INTO_GIFT_CARD,
              icon: 'giftcard',
              position: 0,
            });

            actions[ORDER_ACTIONS.CONFIRMED_BOOKING_TO_GIFTCARD] = () =>
              setOrderAndOpenModal(setIsModalOnHoldToGiftcardOpen);
            break;
          case ORDER_ACTIONS.DOWNLOAD:
            options.push({
              value: ORDER_ACTIONS.DOWNLOAD,
              label:
                order.type === 'giftcard'
                  ? TRANSLATIONS.DOWNLOAD_GIFTCARD
                  : TRANSLATIONS.DOWNLOAD_VOUCHER,
              icon: 'download',
              position: 0,
            });
            actions[ORDER_ACTIONS.DOWNLOAD] = () =>
              setOrderAndOpenModal(setIsModalDownloadFileOpen);
            break;
          case ORDER_ACTIONS.RESEND_EMAIL:
            options.push({
              value: ORDER_ACTIONS.RESEND_EMAIL,
              label: TRANSLATIONS.RESEND_PURCHASE_EMAIL,
              icon: 'document',
              position: 99,
            });
            actions[ORDER_ACTIONS.RESEND_EMAIL] = () =>
              setOrderAndOpenModal(setIsModalResendEmailOpen);
            break;
          default:
            break;
        }
      });
    }

    // Always add the report problem option
    options.push({
      value: ORDER_ACTIONS.REPORT_PROBLEM,
      label: TRANSLATIONS.REPORT_PROBLEM,
      icon: 'warning',
      position: 100,
    });
    actions[ORDER_ACTIONS.REPORT_PROBLEM] = () =>
      setOrderAndOpenModal(setIsModalOrderIssueOpen);

    if (options.length) {
      buttons.push(
        <ActionSelect
          key="options"
          order={order}
          className="entry__cta"
          placeholder={TRANSLATIONS.OPTIONS}
          options={options.sort((a, b) => a.position - b.position)}
          actions={actions}
        />
      );

      return buttons;
    }
  }, [
    order,
    setOrderAndOpenModal,
    onClickDetails,
    setIsModalTransformVoucherOpen,
    setIsModalCancelVoucherOpen,
    setIsModalDownloadFileOpen,
    setIsModalRefundOpen,
    setIsModalOrderIssueOpen,
    setIsModalBookingCompletedToVoucherOpen,
    setIsModalCancelGiftcardOpen,
    setIsModalResendEmailOpen,
    setIsModalOnHoldToVoucherOpen,
  ]);

  return returnButtons();
};

export default ReturnOrderButtons;
